import { useEffect, useEffectEvent, useMemo, useRef } from 'react'
import { getVoiceAssessmentSessionDefinitionIds } from '@/src/config/voice-assessment-session'
import { useAppSession } from '@/src/contexts/AppSessionContext'
import { useContentfulLocale } from '@/src/hooks/useContentfulLocale'
import { useToast } from '@/src/hooks/useToast'
import { backendSessionsQueryOptions } from '@/src/queries/session-queries'
import { appQueryOptions } from '@/src/queries/view-queries'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { mapBackendSessionsToVoiceAssessmentStatistics } from '../utils/map-backend-sessions-to-voice-assessment-statistics'
import {
  isLatestVoiceAssessmentPendingAnalysis,
  VOICE_ASSESSMENT_PENDING_ANALYSIS_POLL_MS,
} from '../utils/voice-assessment-pending-analysis'

interface LatestVoiceAssessmentSnapshot {
  sessionId: number | null
  isAnalyzing: boolean
}

export interface UseVoiceAssessmentAnalysisReadyToastOptions {
  /**
   * When false, skips the subscription and clears transition memory so a later
   * enable does not treat the first fetch as a false “analysis finished” edge.
   */
  enabled?: boolean
}

/**
 * Shows a success toast when the latest voice assessment leaves the analyzing
 * state and results are available. Uses the shared backend sessions query with
 * polling only while the latest voice assessment is `PendingAnalysis`.
 */
export function useVoiceAssessmentAnalysisReadyToast(
  options?: UseVoiceAssessmentAnalysisReadyToastOptions,
) {
  const enabled = options?.enabled

  const { t } = useTranslation(['statistics'])
  const { showToast } = useToast()
  const showAnalysisFinishedToast = useEffectEvent((completed: boolean) => {
    if (completed) {
      showToast({
        variant: 'success',
        title: t('statistics:voice_assessment_analysis_ready_title'),
        message: t('statistics:voice_assessment_analysis_ready_message'),
      })
      return
    }

    // A session that leaves analyzing without completing has been `Aborted`
    // by the backend, which is how a failed analyze surfaces.
    showToast({
      variant: 'error',
      title: t('statistics:voice_assessment_analysis_failed_title'),
      message: t('statistics:voice_assessment_analysis_failed_message'),
    })
  })

  const contentfulLanguage = useContentfulLocale()
  const { backendUser } = useAppSession()
  const {
    data: sessionDefinitionIds,
    isPending: isSessionDefinitionIdsPending,
  } = useQuery({
    ...appQueryOptions(contentfulLanguage),
    enabled: enabled && backendUser != null,
    select: getVoiceAssessmentSessionDefinitionIds,
  })

  const sessionsQuery = useQuery({
    ...backendSessionsQueryOptions,
    enabled: enabled && sessionDefinitionIds != null,
    refetchInterval: (query) =>
      enabled &&
      sessionDefinitionIds != null &&
      isLatestVoiceAssessmentPendingAnalysis(
        query.state.data,
        sessionDefinitionIds,
      )
        ? VOICE_ASSESSMENT_PENDING_ANALYSIS_POLL_MS
        : false,
  })

  const data = useMemo(() => {
    if (sessionsQuery.data == null) {
      return undefined
    }
    if (sessionDefinitionIds == null) {
      return undefined
    }
    return mapBackendSessionsToVoiceAssessmentStatistics(
      sessionsQuery.data,
      sessionDefinitionIds,
      {
        exerciseCount: 1,
      },
    )
  }, [sessionDefinitionIds, sessionsQuery.data])

  const isPending = sessionsQuery.isPending || isSessionDefinitionIdsPending
  const previousLatestVoiceAssessmentRef =
    useRef<LatestVoiceAssessmentSnapshot | null>(null)

  useEffect(() => {
    if (!enabled) {
      previousLatestVoiceAssessmentRef.current = null
      return
    }

    if (isPending || data == null) {
      return
    }

    const latest = data.entries.find((item) => item.isLatest)
    const isAnalyzing =
      data.status === 'analyzing' || latest?.rowStatus === 'analyzing'
    const latestSessionId = latest?.backendSessionId ?? null
    const previousSnapshot = previousLatestVoiceAssessmentRef.current

    if (previousSnapshot === null) {
      previousLatestVoiceAssessmentRef.current = {
        sessionId: latestSessionId,
        isAnalyzing,
      }
      return
    }

    const sameLatestSession =
      previousSnapshot.sessionId != null &&
      latestSessionId != null &&
      previousSnapshot.sessionId === latestSessionId

    if (
      sameLatestSession &&
      previousSnapshot.isAnalyzing &&
      !isAnalyzing &&
      latest != null
    ) {
      showAnalysisFinishedToast(latest.rowStatus === 'completed')
    }

    previousLatestVoiceAssessmentRef.current = {
      sessionId: latestSessionId,
      isAnalyzing,
    }
  }, [data, enabled, isPending])
}
